import React, {useState} from "react"
import {Link as RouterLink, useLocation, useNavigate} from "react-router-dom"
import {IconButton, Link, Menu, MenuItem, Skeleton, Stack, Tooltip} from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import {ProjectRequests} from "../../../api/requests/ProjectRequests"
import {ProjectJson} from "../../../api/json/ProjectJson"

const MAX_NAME_LENGTH = 28

const linkSx = {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#333',
    textDecoration: 'none',
    '&:hover': {textDecoration: 'underline'},
}

function truncate(label: string): {display: string; truncated: boolean} {
    if (label.length <= MAX_NAME_LENGTH) return {display: label, truncated: false}
    return {display: label.slice(0, MAX_NAME_LENGTH - 1) + "…", truncated: true}
}

interface ProjectCrumbProps {
    projectId: string
    name?: string
}

function ProjectCrumb({projectId, name}: ProjectCrumbProps) {
    const location = useLocation()
    const navigate = useNavigate()

    const [projects, setProjects] = useState<ProjectJson[]>([])
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement>()

    const pathComponents = location.pathname.split("/")
    const projectSubpage = pathComponents.length > 3 ? pathComponents[3] : undefined

    const openSwitcher = (anchor: HTMLElement) => {
        ProjectRequests.list()
            .then(response => setProjects(response.content.slice(0, 10)))
            .catch(() => setProjects([]))
        setMenuAnchor(anchor)
    }

    const selectProject = (nextId: string) => {
        setMenuAnchor(undefined)
        navigate(`/projects/${nextId}/${projectSubpage ?? ""}`)
    }

    const {display, truncated} = name ? truncate(name) : {display: "", truncated: false}

    return (
        <Stack direction="row" alignItems="center" spacing={0.25}>
            {name
                ? (
                    truncated
                        ? (
                            <Tooltip title={name}>
                                <Link component={RouterLink} to={`/projects/${projectId}`} sx={linkSx}>{display}</Link>
                            </Tooltip>
                        )
                        : <Link component={RouterLink} to={`/projects/${projectId}`} sx={linkSx}>{display}</Link>
                )
                : <Skeleton variant="text" width={80} sx={{fontSize: '0.95rem'}}/>
            }
            <IconButton
                size="small"
                onClick={e => openSwitcher(e.currentTarget)}
                aria-label="Switch project"
                sx={{p: 0.25, color: '#999'}}
            >
                <ArrowDropDownIcon fontSize="small"/>
            </IconButton>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(undefined)}
            >
                {projects.map(p => (
                    <MenuItem
                        key={p.id}
                        selected={p.id === projectId}
                        onClick={() => selectProject(p.id)}
                    >
                        {p.name}
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    )
}

export default ProjectCrumb
